import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectCustomer, selectFetchedSalesPartners, setCustomer } from '@oneqrew-partnertool/core';
import { FaqsComponent } from '@oneqrew-partnertool/faqs';
import { Customer, SalesPartner } from '@oneqrew-partnertool/models';
import { OrderContactComponent } from '@oneqrew-partnertool/order-contact';
import { OrderDetailsComponent } from '@oneqrew-partnertool/order-details';
import { OrderOverviewComponent } from '@oneqrew-partnertool/order-overview';
import { OrderSummaryComponent } from '@oneqrew-partnertool/order-summary';
import { OrderCustomerInfoComponent } from '@oneqrew-partnertool/order-customer-info';
import { PageGuard, RouteParam, SharedModule } from '@oneqrew-partnertool/utils';
import { DialogModule } from 'primeng/dialog';
import { InputOtpModule } from 'primeng/inputotp';
import { CustomerVerificationDialogComponent } from '@oneqrew-partnertool/customer-verification-dialog'
import { take } from 'rxjs';
import { CustomerVerificationService } from '../../../../utils/src/lib/customer-verification.service';

@Component({
  selector: 'app-order-page',
  standalone: true,
  imports: [CustomerVerificationDialogComponent, FormsModule, OrderOverviewComponent, FaqsComponent, OrderContactComponent, OrderDetailsComponent, OrderSummaryComponent, OrderCustomerInfoComponent, SharedModule, DialogModule, InputOtpModule],
  templateUrl: './order-page.component.html'
})
export class OrderPageComponent implements OnInit, OnDestroy {
  orderForm!: FormGroup;
  userID: string = '';
  customer!: Customer;
  @Input() isNewPlanPage = false;
  orderFormValid!: boolean;
  verifyCustomer: boolean = true;
  otpCode!: string;
  customerOfferId!: number
  salesPartners!: Map<number | undefined, SalesPartner>;
  proceedToCheckout: boolean = false;

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private pageGuard: PageGuard,
    private customerVerificationService: CustomerVerificationService
  ) {
    const offerId = route.snapshot.queryParams['offerId']
    const accessCode = route.snapshot.queryParams['accessCode']
    const customerOffer = customerVerificationService.get(offerId, accessCode)
    if (customerOffer) {
      this.verifyCustomer = false;
    }
  }

  ngOnDestroy() {
    if (!this.proceedToCheckout) {
      this.updateCustomer()
    }
  }

  ngOnInit(): void {
    this.orderForm = this.fb.group({});
    this.orderForm.statusChanges.subscribe((status) => {
      this.orderFormValid = status == "VALID";
    });

    this.customerOfferId = this.route.snapshot.queryParams['offerId'];

    this.route.data.subscribe((data: { [key: string]: any }) => {
      this.isNewPlanPage = data['isNewPlanPage'];
    })

    this.store.select(selectFetchedSalesPartners)
      .pipe(take(1))
      .subscribe(salesPartners => {
        this.salesPartners = new Map(salesPartners.map(obj => [obj.id, obj]))
      });

    this.userID = RouteParam.getParamAsString(this.route, 'id');
    this.store.select(selectCustomer).subscribe((customer) => {
      this.customer = customer;
    });

    if (!this.isNewPlanPage && (!this.customer || Object.keys(this.customer).length === 0 || !this.customer.customerCode)) {
      this.router.navigate(['/']);
    }

  }

  closeDialog() {
    this.verifyCustomer = false
  }

  onFormSubmit() {
    if (this.orderForm.valid) {
      this.updateCustomer()
      this.pageGuard.setNavigatedFromValidUrl(true);
      this.proceedToCheckout = true;
      if (this.isNewPlanPage) {
        this.router.navigate(['/bestellung/checkout']);
      } else {
        this.router.navigate(['/umstellung/checkout/', this.userID]);
      }
    } else {
      this.orderForm.markAllAsTouched();
      console.error('Form is invalid');
    }
  }

  formInitialized(form: FormGroup) {
    for (const proprty in form.controls) {
      this.orderForm.setControl(proprty, form.controls[proprty]);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    $event.returnValue = 'Beim erneuten Laden der Seite gehen die ausgewählten Module und der Plan verloren.';
  }

  private updateCustomer() {
    const value = this.orderForm.value;
    const salesPartner = this.salesPartners.get(value.salesPartner);

    const updatedCustomer = {
      ...this.customer,
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      confirmEmail: value.confirmEmail,
      company: value.company,
      street: value.street,
      addressTwo: value.addressTwo,
      postCode: value.postCode,
      city: value.city,
      salutation: value.salutation,
      salutationOpener: value.salutationOpener,
      bsNumber: value.bsNumber,
      ustIdNr: value.ustIdNr,
      salesPartner: salesPartner ?? this.customer.salesPartner,
      phoneNr: value.phoneNr,
      mobilePhoneNr: value.mobilePhoneNr,
      knr: value.bsNumber ?? this.customer.knr
    };
    this.store.dispatch(setCustomer(updatedCustomer));
  }
}
